import { Badge } from '@hub/ui';
import { Lock } from 'lucide-react';
import type { ViewPrimitive, BladeContext } from '../registry';
import { BladeLoading, BladeError, BladeDenied, BladeEmpty, forcedBladeState } from '../BladeStates';
import { useBladeEntity, useViewDefinition, type BladeField } from '../BladeDataSource';
import { resolveRecordSections } from '../binding/resolveRecord';

// Record/detail view-primitive (HUB-276/277) — an entity detail (status badge,
// key stats, field grid, related list) bound through the BladeDataSource. A
// bindingless blade (no entityId) shows the empty state; loading/error/denied
// delegate to the state catalog. Content layer — brand utilities.
//
// HUB-408: the field grid is now DATA-DRIVEN. When an active `view` definition
// resolves for the blade's view key, its sections/fields decide the record's
// field order, labels, grouping, and render hints (the first real consumption
// of the HUB-277 binding renderer). Absent a definition, the primitive falls
// back to the source's hardcoded field list — zero regression.

/** The view key the record primitive resolves its layout from. Overridable per
 *  blade via `params.view`; defaults to the seeded work-item detail view. */
const DEFAULT_RECORD_VIEW = 'workitem_detail';

/** Field grid cells for one dl (shared by the flat fallback and each resolved
 *  section) — restricted values render the HUB-357 placeholder. */
function FieldRows({ fields }: { fields: BladeField[] }) {
  return (
    <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-sm">
      {fields.map((f) => (
        <div key={f.key ?? f.label} className="contents">
          <dt className="text-muted-foreground">{f.label}</dt>
          {f.restricted ? (
            // Server-side redaction (HUB-357): the value never reached the
            // client. Show that the field exists but is restricted.
            <dd className="flex items-center gap-1.5 text-muted-foreground">
              <Lock aria-hidden className="size-3.5 shrink-0" />
              <span className="italic">Restricted</span>
            </dd>
          ) : (
            <dd className={f.mono ? 'font-mono text-foreground' : 'text-foreground'}>
              {f.value}
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}

function RecordBody({ ctx }: { ctx: BladeContext }) {
  const { status, data } = useBladeEntity(ctx.descriptor);
  const viewKey = ctx.descriptor.params?.view ?? DEFAULT_RECORD_VIEW;
  const { data: viewDef } = useViewDefinition(viewKey);

  if (status === 'loading') return <BladeLoading />;
  if (status === 'error') return <BladeError />;
  if (status === 'denied') return <BladeDenied />;
  if (!data) {
    return <BladeEmpty title="No record selected" description="Choose an item from the worklist." />;
  }

  const { badge, stats = [], fields, related = [] } = data;

  // Data-driven layout: a resolved definition drives the field grid; if it
  // resolves nothing usable, fall back to the flat hardcoded field list.
  const sections = viewDef ? resolveRecordSections(viewDef, fields) : [];

  return (
    <div className="flex flex-col gap-5">
      {badge && (
        <div className="flex items-center gap-2">
          <Badge variant="success">{badge}</Badge>
        </div>
      )}

      {stats.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="rounded-coronis border border-border px-3 py-2.5">
              <div className="text-xs text-muted-foreground">{s.label}</div>
              <div className="mt-0.5 text-lg font-semibold text-foreground">{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {sections.length > 0 ? (
        sections.map((section) => (
          <div key={section.id}>
            {section.label && (
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.label}
              </div>
            )}
            <FieldRows fields={section.fields} />
          </div>
        ))
      ) : (
        <FieldRows fields={fields} />
      )}

      {related.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Related claims
          </div>
          <ul role="list" className="flex flex-col">
            {related.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2.5 text-left transition-colors hover:bg-muted"
                  onClick={(e) => {
                    e.stopPropagation();
                    ctx.openChild({
                      key: 'form',
                      title: c.id,
                      eyebrow: 'Claim',
                      entityId: c.id,
                      width: 'wide',
                    });
                  }}
                >
                  <span className="font-mono text-sm text-foreground">{c.id}</span>
                  {c.meta && <span className="text-xs text-muted-foreground">{c.meta}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const recordPrimitive: ViewPrimitive = {
  key: 'record',
  render: (ctx) => {
    // Forced states + bindingless empty short-circuit before the data hook.
    const forced = forcedBladeState(ctx.descriptor);
    if (forced === 'loading') return <BladeLoading />;
    if (forced === 'error') return <BladeError />;
    if (forced === 'denied') return <BladeDenied />;
    if (forced === 'empty' || !ctx.descriptor.entityId) {
      return <BladeEmpty title="No record selected" description="Choose an item from the worklist." />;
    }
    return <RecordBody ctx={ctx} />;
  },
};
